import { t } from "../../../i18n";
import { formatCurrency, formatPeriod } from "../../../lib/format";
import { formatDueRelative, getSettlementStatus } from "../../../lib/settlementStatus";
import { Badge, Card, highlightMatch, Icon } from "../../ui";
import type { SettlementListItem } from "../utils";

interface SettlementCardProps {
  settlement: SettlementListItem;
  onClick: () => void;
  search?: string;
}

/**
 * Single-settlement card rendered in the settlements tab. The card is
 * interactive — clicking anywhere on it navigates to the settlement
 * detail view (handled by the parent passing an `onClick`).
 */
export function SettlementCard({ settlement, onClick, search = "" }: SettlementCardProps) {
  const statusInfo = getSettlementStatus({
    status: settlement.status,
    endDate: settlement.endDate,
  });
  const showDueHint = statusInfo.status !== "locked" && statusInfo.status !== "awaiting_approval";

  return (
    <Card interactive onClick={onClick} padding="md">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-semibold text-base text-ink-900">
              {highlightMatch(settlement.title, search)}
            </h3>
            <Badge tone={statusInfo.tone} iconLeft={statusInfo.icon}>
              {statusInfo.label}
            </Badge>
          </div>
          <p className="mt-1 text-ink-500 text-xs">
            {formatPeriod(settlement.startDate, settlement.endDate)}
            {showDueHint && (
              <>
                {" · "}
                <span
                  className={
                    statusInfo.status === "overdue"
                      ? "font-medium text-rose-600 dark:text-rose-400"
                      : undefined
                  }
                >
                  {formatDueRelative(settlement.endDate)}
                </span>
              </>
            )}
          </p>
          <div className="mt-2 flex items-center gap-3 text-ink-500 text-xs">
            <span className="inline-flex items-center gap-1">
              <Icon name="file-text" size="xs" />
              {t.household.settlements.invoiceCount(settlement.invoiceCount)}
            </span>
            <span className="inline-flex items-center gap-1">
              <Icon name="receipt" size="xs" />
              {t.household.settlements.expenseCount(settlement.transactionCount)}
            </span>
          </div>
        </div>
        <div className="shrink-0 text-right">
          <div className="tabular font-semibold text-base text-ink-900">
            {formatCurrency(settlement.totalAmount, settlement.currency)}
          </div>
          <Icon name="chevron-right" size="sm" className="mt-1 ml-auto text-ink-400" />
        </div>
      </div>
    </Card>
  );
}
